import { useContext, useEffect, useState } from "react";
import PageTitle from "../pageTitle";
import ContactForm from "../../cards/contactForm";
import MailSubscription from "../../cards/emailSub";
import { Helmet } from "react-helmet-async";
import BlogCard from "../../cards/blogCard";
import { Link, useSearchParams } from "react-router-dom";
import { BlogContext } from "../../../App";
import axios from "axios";
import usePagination from "../../../hooks/usePagination";
import SearchForm from "../../../utils/searchForm";
import ServicePageSkeleton from "../skeletons/servicePage";

function Items({ currentItems }: any) {
  return (
    <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-6 sm:cht-gap-8">
      {currentItems &&
        currentItems?.map((item: any) => (
          <BlogCard
            key={item?.id || item?.slug}
            id={item?.id}
            slug={item?.slug}
            title={item?.title}
            description={item?.safe_post_content_html}
            pub_date={item?.pub_date}
            image_url={item?.image}
            category={item?.category?.name}
            total_comments={item?.total_comments}
            total_likes={item?.total_likes}
          />
        ))}
    </div>
  );
}

const Blog = () => {
  const { blog, blogLoading } = useContext(BlogContext);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [CategoriesLoading, setCategoriesLoading] = useState(false);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [onSearchTerm, setOnSearchTerm] = useState("");

  const [searchParams] = useSearchParams();
  const tagParams = searchParams.get("tag");
  const catParams = searchParams.get("category");
  const searchTermParams = searchParams.get("search");

  const { paginationUI } = usePagination({
    itemsPerPage: 6,
    data: filteredBlogs.sort((a, b) => b.id - a.id) || [],
    children: <Items />,
  });

  // if (searchTermParams) {
  //     // searchParams.append(searchTerm, searchTerm);
  //     alert(searchTerm);
  // }

  useEffect(() => {
    const filterBlogs = () => {
      if (tagParams) {
        return blog.filter((blog) =>
          blog.tags.some((tag) => tag.name === tagParams)
        );
      } else if (catParams) {
        return blog.filter((blog) => blog.category.name === catParams);
      } else if (searchTermParams) {
        return blog.filter(
          (blog) =>
            blog.title.toLowerCase().includes(searchTermParams.toLowerCase()) ||
            blog.content
              .toLowerCase()
              .includes(searchTermParams.toLowerCase()) ||
            blog.tags.some((tag) =>
              tag.name.toLowerCase().includes(searchTermParams.toLowerCase())
            )
        );
      }
      // Default to all blogs if no parameters are present
      return blog;
    };

    setFilteredBlogs(filterBlogs());
  }, [blog, tagParams, catParams, searchTermParams, blogLoading]);

  useEffect(() => {
    const fetchTagData = async () => {
      setTagsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/blog/tags`
        );
        setTags(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setTagsLoading(false);
      }
    };
    fetchTagData();
  }, []);

  useEffect(() => {
    const fetchCategoryData = async () => {
      setCategoriesLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/blog/category`
        );
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategoryData();
  }, []);
  return (
    <>
      <Helmet>
        <title>Blog - Codehub Technologies</title>
      </Helmet>
      <PageTitle
        title={
          tagParams
            ? `Tag: ${tagParams}`
            : catParams
            ? `Category: ${catParams}`
            : "Our Blog"
        }
      />

      <section className="cht-max-w-7xl cht-mx-auto cht-px-4 sm:cht-px-6 lg:cht-px-8 cht-py-12 sm:cht-py-16 !cht-font-body">
        {Object.keys(filteredBlogs).length > 0 && (
          <div className="cht-mb-6 lg:cht-hidden">
            <SearchForm placeholder="Search articles..." onSearch={setOnSearchTerm} />
          </div>
        )}

        <div className="cht-grid lg:cht-grid-cols-12 cht-gap-8 lg:cht-gap-10">
          {/* Main Articles List Column */}
          <div className="lg:cht-col-span-8">{paginationUI}</div>

          {/* Sidebar Column */}
          <div className="lg:cht-col-span-4 cht-space-y-6">
            {Object.keys(filteredBlogs).length > 0 && (
              <div className="cht-hidden lg:cht-block">
                <SearchForm placeholder="Search articles..." onSearch={setOnSearchTerm} />
              </div>
            )}

            {Object.keys(categories).length > 0 && (
              <div className="cht-bg-white cht-rounded-[20px] cht-border cht-border-slate-200/80 cht-shadow-soft cht-p-6">
                <h4 className="cht-text-base cht-font-bold !cht-font-heading cht-text-[#1e293b] cht-mb-4 cht-border-b cht-border-slate-100 cht-pb-3">
                  Categories
                </h4>
                <div className="cht-flex cht-flex-wrap cht-gap-2">
                  {categories?.map((item: any) => (
                    <Link
                      to={`/blog?category=${item?.name}`}
                      className="cht-inline-flex cht-items-center cht-px-3.5 cht-py-1.5 cht-rounded-full cht-text-xs cht-font-medium cht-bg-slate-100 hover:cht-bg-[#3e57da] hover:cht-text-white cht-text-slate-600 cht-transition-all !cht-no-underline"
                      key={item.id}
                    >
                      {item?.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(tags).length > 0 && (
              <div className="cht-bg-white cht-rounded-[20px] cht-border cht-border-slate-200/80 cht-shadow-soft cht-p-6">
                <h4 className="cht-text-base cht-font-bold !cht-font-heading cht-text-[#1e293b] cht-mb-4 cht-border-b cht-border-slate-100 cht-pb-3">
                  Tags
                </h4>
                <div className="cht-flex cht-flex-wrap cht-gap-1.5">
                  {tags?.map((item: any) => (
                    <Link
                      to={`/blog?tag=${item?.name}`}
                      className="cht-inline-flex cht-items-center cht-px-3 cht-py-1 cht-rounded-lg cht-text-xs cht-font-medium cht-bg-slate-50 cht-border cht-border-slate-200/80 hover:cht-border-[#3e57da] hover:cht-text-[#3e57da] cht-text-slate-600 cht-transition-all !cht-no-underline"
                      key={item.id}
                    >
                      #{item?.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {blogLoading && <ServicePageSkeleton numberOfCols={2} />}

      <MailSubscription />
      <ContactForm />
    </>
  );
};

export default Blog;
